import type { CardStatisticsBalance } from './card.types';

const MONTH_KEY = /^(\d{4})-(\d{2})$/;

/**
 * Chart labels for `monthly[].month` keys (`YYYY-MM`) in the user's language:
 * `Aug` / `Авг`. When the series spans more than a year the same month name
 * would appear twice, so every label gets a two-digit year: `Aug ’25`.
 */
export function monthLabels(months: readonly string[], lang: string): string[] {
  const parsed = months.map(parseMonthKey);
  const withYear = hasDuplicateMonthNumbers(parsed);
  const formatter = new Intl.DateTimeFormat(lang, { month: 'short', timeZone: 'UTC' });

  return months.map((month, index) => {
    const key = parsed[index];
    if (!key) return month;
    // Date.UTC + timeZone: 'UTC' — otherwise negative offsets render the previous month.
    const raw = formatter.format(new Date(Date.UTC(key.year, key.monthIndex, 1)));
    const short = capitalize(raw.replace(/\.$/, '')); // ru: 'авг.' → 'Авг'
    return withYear ? `${short} ’${String(key.year).slice(-2)}` : short;
  });
}

interface MonthKey {
  readonly year: number;
  readonly monthIndex: number;
}

function parseMonthKey(month: string): MonthKey | null {
  const match = MONTH_KEY.exec(month);
  if (!match) return null;
  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  return monthIndex >= 0 && monthIndex <= 11 ? { year, monthIndex } : null;
}

/** Only well-formed keys count — a malformed one must not force years onto every label. */
function hasDuplicateMonthNumbers(keys: readonly (MonthKey | null)[]): boolean {
  const seen = new Set<number>();
  for (const key of keys) {
    if (!key) continue;
    if (seen.has(key.monthIndex)) return true;
    seen.add(key.monthIndex);
  }
  return false;
}

function capitalize(value: string): string {
  return value.length === 0 ? value : value.charAt(0).toLocaleUpperCase() + value.slice(1);
}

/** Merchant share of the period's spend as a whole percent (floor); `null` when there is nothing to divide by. */
export function merchantSharePercent(spentMinor: bigint, totalSpentMinor: bigint): number | null {
  if (totalSpentMinor <= 0n) return null;
  return Number((spentMinor * 100n) / totalSpentMinor);
}

export interface CurrencyBalance {
  readonly currencyCode: string;
  readonly currencyPrecision: number;
  readonly balanceMinor: bigint;
}

/** Sums per-card balances by currency, keeping the order in which currencies first appear. */
export function aggregateBalancesByCurrency(
  balances: readonly CardStatisticsBalance[],
): CurrencyBalance[] {
  const byCurrency = new Map<string, CurrencyBalance>();
  for (const balance of balances) {
    const current = byCurrency.get(balance.currencyCode);
    byCurrency.set(
      balance.currencyCode,
      current
        ? { ...current, balanceMinor: current.balanceMinor + balance.balanceMinor }
        : {
            currencyCode: balance.currencyCode,
            currencyPrecision: balance.currencyPrecision,
            balanceMinor: balance.balanceMinor,
          },
    );
  }
  return [...byCurrency.values()];
}
